import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  Col,
  Collapse,
  CollapseProps,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Space,
  Statistic,
  Tag,
  theme,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useDelayedLoading } from '@/hooks';
import { queryKeys } from '@/lib/react-query';
import { BillableServiceBooking, BookingType, CancellationFee } from '@/models';
import { ROUTE_CONSTANTS } from '@/routes';
import {
  formatCurrency,
  getBookingTypeIcon,
  getStatusColor,
  isUserAdmin,
  MONTH_YEAR_FORMAT,
  showMessage,
} from '@/utils';
import { useBillableBookings, useBillableBookingsExport } from '@/web-api/bookings';
import { useUpdateCancellationFee } from '@/web-api/resources';

type ResourceStatsProps = {
  resourceId: string;
};

export function ResourceStats({ resourceId }: ResourceStatsProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth0();
  const isAdmin = isUserAdmin(user);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [form] = Form.useForm<CancellationFee>();
  const {
    token: { colorPrimary },
  } = theme.useToken();
  const { t } = useTranslation();
  const { data, isLoading } = useBillableBookings({
    resourceId: resourceId,
    month: selectedDate.month(),
    year: selectedDate.year(),
  });
  const { mutate: billableBookingsExportMutate, isPending: exportIsLoading } =
    useBillableBookingsExport();
  const showDelayedLoading = useDelayedLoading(isLoading);
  const billableCount = data?.billableBookings?.length ?? 0;
  const canceledCount = data?.cancellationFees?.length ?? 0;
  const { mutate: updateCancellationFeeMutate, isPending: updateCancellationFeeIsLoading } =
    useUpdateCancellationFee();

  function onMonthChange(date: Dayjs | null) {
    if (date) {
      setSelectedDate(date);
    }
  }

  function openBillableBookingsExport() {
    billableBookingsExportMutate(
      {
        resourceId: resourceId,
        month: selectedDate.month(),
        year: selectedDate.year(),
      },
      {
        onSuccess: (data) => {
          window.open(URL.createObjectURL(data), '_blank');
        },
      }
    );
  }

  function onItemEdit(item: CancellationFee) {
    form.setFieldsValue(item);
    setIsCancellationModalOpen(true);
  }

  function onCancellationOk() {
    const fee = form.getFieldsValue();

    updateCancellationFeeMutate(
      { resourceId, fee },
      {
        onSuccess: () => {
          const params = {
            resourceId: resourceId,
            month: selectedDate.month(),
            year: selectedDate.year(),
          };
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({
            queryKey: queryKeys.resourceBookings.billable(params),
          });
          setIsCancellationModalOpen(false);
          form.resetFields();
        },
      }
    );
  }

  function onCancellationCancel() {
    setIsCancellationModalOpen(false);
    form.resetFields();
  }

  const items: CollapseProps['items'] = [
    {
      key: 'verified',
      label: t('common:completed'),
      children: (
        <List
          size='small'
          dataSource={data?.billableBookings}
          renderItem={(item: BillableServiceBooking) => (
            <List.Item
              extra={
                <Link to={`/admin/${ROUTE_CONSTANTS.BOOKINGS_PATH}/${item.id}`}>
                  {t('common:show')}
                </Link>
              }
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{ backgroundColor: colorPrimary }}
                    icon={React.createElement(getBookingTypeIcon(item.type), {
                      style: {
                        rotate: item.type === BookingType.PHONE ? '90deg' : '',
                      },
                    })}
                  />
                }
                title={
                  <Space separator={'-'}>
                    <Typography.Text>{item.reference}</Typography.Text>
                    <Typography.Text>{item.customer}</Typography.Text>
                    <Typography.Text> {item.date}</Typography.Text>
                  </Space>
                }
                description={
                  <>
                    <Tag key={item.id} color={getStatusColor(item.status)}>
                      {t(item.status)}
                    </Tag>
                    <Typography.Text>{formatCurrency(undefined, item.total)}</Typography.Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
      ),
    },
    {
      key: 'canceled',
      label: t('common:CANCELED'),
      children: (
        <List
          size='small'
          dataSource={data?.cancellationFees ?? []}
          renderItem={(item: CancellationFee) => (
            <List.Item
              actions={
                isAdmin
                  ? [
                      <Button
                        type='link'
                        key={item.id}
                        onClick={() => onItemEdit(item)}
                        disabled={!isAdmin}
                      >
                        {t('forms:edit')}
                      </Button>,
                    ]
                  : []
              }
            >
              <List.Item.Meta
                title={`${item.name}: ${formatCurrency(undefined, item.value)}`}
                description={item.description}
              />
            </List.Item>
          )}
        />
      ),
    },
  ];

  return (
    <>
      <Card
        title={t('common:bookings')}
        extra={
          <Space>
            <DatePicker
              aria-label='Monat auswählen'
              picker='month'
              value={selectedDate}
              onChange={onMonthChange}
              format={MONTH_YEAR_FORMAT}
              allowClear={false}
              style={{ width: 150 }}
            />
            <Button type='primary' onClick={openBillableBookingsExport} loading={exportIsLoading}>
              {t('common:showAsPDF')}
            </Button>
          </Space>
        }
        loading={showDelayedLoading}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <Statistic title={t('common:completed')} value={billableCount} />
          </Col>
          <Col xs={24} md={8}>
            <Statistic title={t('common:CANCELED')} value={canceledCount} />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title={t('common:billable')}
              value={Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                data?.billableTotal ?? 0
              )}
            />
          </Col>
          <Col span={24}>
            <Collapse items={items} defaultActiveKey={['verified']} />
          </Col>
        </Row>
      </Card>
      <Modal
        title={t('common:cancellationFees')}
        open={isCancellationModalOpen}
        okText={t('forms:save')}
        onOk={form.submit}
        onCancel={onCancellationCancel}
        okButtonProps={{ loading: updateCancellationFeeIsLoading, disabled: !isAdmin }}
        cancelButtonProps={{ loading: updateCancellationFeeIsLoading }}
      >
        <Form form={form} layout='vertical' onFinish={onCancellationOk}>
          <Form.Item name='id' hidden />
          <Form.Item label={t('forms:name')} name='name' rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('forms:description')} name='description' rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('forms:amount')} name='value' rules={[{ required: true }]}>
            <Space.Compact block>
              <InputNumber<number>
                precision={2}
                controls={false}
                formatter={(value) => (value ? `${value}` : '')}
                onChange={(value) => value && form.setFieldsValue({ value })}
                decimalSeparator=','
              />
              <Space.Addon>€</Space.Addon>
            </Space.Compact>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
